import React, { useEffect, useState } from 'react'

import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
} from '@tanstack/react-query'
import FormValidationErrorMessage from '@zet/customer/src/components/formValidationErrorMessage'
import {
  B1,
  B2,
  B5,
  BottomSheet,
  Icon,
  Input,
  SH1,
  Select,
  Stack,
  XStack,
  colors,
} from '@zet/design-system'
import Pressable from '@zet/design-system/pressable'
import { i18n } from '@zet/localization'
import { subYears } from 'date-fns'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form'

import { IGetCriterialIndexesResponse } from '../../../../api/brands/types'
import { DateField } from '../basicDetails/dateField'

const FormFields = ({
  control,
  errors,
  setValue,
  criteriaData,
  refetch,
  getValues,
}: {
  control: Control<
    {
      panNumber: string
      dob: Date
      salaryType: string
      employmentType: string
      income: string
    },
    any
  >
  errors: FieldErrors<{
    panNumber: string
    dob: Date
    salaryType: string
    employmentType: string
    income: string
  }>
  setValue: UseFormSetValue<{
    panNumber: string
    dob: Date
    salaryType: string
    employmentType: string
    income: string
  }>
  criteriaData: IGetCriterialIndexesResponse | undefined
  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined,
  ) => Promise<QueryObserverResult<IGetCriterialIndexesResponse, unknown>>
  getValues: UseFormGetValues<{
    panNumber: string
    dob: Date
    salaryType: string
    employmentType: string
    income: string
  }>
}) => {
  const [salaryType, setSalaryType] = useState<string>(
    getValues('salaryType') || '',
  )
  const [employmentType, setEmploymentType] = useState<string>('')
  const [isEmploymentSheetVisible, setIsEmploymentSheetVisible] =
    useState<boolean>(false)
  const [annualIncome, setAnnualIncome] = useState<string>(
    getValues('income') || '',
  )

  function selectEmploymentType(value: string) {
    setIsEmploymentSheetVisible(false)
    setValue('employmentType', value, {
      shouldValidate: true,
      shouldTouch: true,
    })
    setEmploymentType(value)
  }

  function closeBottomsheet() {
    setIsEmploymentSheetVisible(false)
  }

  useEffect(() => {
    refetch()
  }, [i18n.locale])

  return (
    <Stack className="px-3">
      <Stack className="mt-3">
        <B2 className="text-grays-80 mb-1">
          {i18n.t('webFlow.basicDetails.pan')}{' '}
          <B1 className="text-error-100 ml-1">*</B1>
        </B2>
        <Controller
          control={control}
          render={({
            field: { onChange, onBlur, value },
            fieldState: { isTouched },
          }) => (
            <Input
              maxLength={10}
              placeholder="Eg: BHWIC2448S"
              onBlur={onBlur}
              onChangeText={onChange}
              value={value}
              type={
                Boolean(errors?.panNumber?.message) && isTouched
                  ? 'error'
                  : errors?.panNumber?.message ===
                      i18n.t('webFlow.basicDetails.panExist')
                    ? 'warning'
                    : 'primary'
              }
            />
          )}
          name="panNumber"
        />
        <FormValidationErrorMessage
          error={errors?.panNumber?.message}
          key={errors?.panNumber?.message}
        />
      </Stack>

      <Stack className="mt-3">
        <B2 className=" text-grays-80 mb-1">
          {i18n.t('webFlow.basicDetails.income')}{' '}
          <B1 className="text-error-100 ml-1">*</B1>
        </B2>
        <Controller
          control={control}
          render={({ field: { onChange, onBlur } }) => (
            <XStack>
              <Pressable
                onBlur={onBlur}
                onPress={() => {
                  onChange('Bank')
                  setSalaryType('Bank')
                }}
              >
                <XStack
                  className={`px-4/2 mr-3 items-center rounded-xl border py-2
             ${
               'Bank' !== salaryType
                 ? 'border-grays-40'
                 : 'border-primary-100 bg-grays-20'
             }
            `}
                >
                  {salaryType !== 'Bank' ? (
                    <Icon type="BankAccount" size={36}></Icon>
                  ) : (
                    <Icon type="BankAccountWithColor" size={36}></Icon>
                  )}
                  <B2 className="ml-4/2 text-grays-60">
                    {i18n.t('customerDetails.bank')}
                  </B2>
                </XStack>
              </Pressable>
              <Pressable
                onBlur={onBlur}
                onPress={() => {
                  onChange('Cash')
                  setSalaryType('Cash')
                }}
              >
                <XStack
                  className={`px-4/2 mr-3 items-center rounded-xl border py-2
             ${
               'Cash' !== salaryType
                 ? 'border-grays-40'
                 : 'border-primary-100 bg-grays-20'
             }
            `}
                >
                  {salaryType !== 'Cash' ? (
                    <Icon type="Cash" size={36}></Icon>
                  ) : (
                    <Icon type="CashWithColor" size={36}></Icon>
                  )}
                  <B2 className="ml-4/2 text-grays-60">
                    {i18n.t('customerDetails.cash')}
                  </B2>
                </XStack>
              </Pressable>
            </XStack>
          )}
          name="salaryType"
        />
        <FormValidationErrorMessage
          error={errors?.salaryType?.message}
          key={errors?.salaryType?.message}
        />
      </Stack>

      <Stack className="mt-3">
        <B2 className="text-grays-80 mb-2">
          {i18n.t('webFlow.employmentDetails.type')}
          <B1 className="text-error-100 pl-1">*</B1>
        </B2>
        <Controller
          control={control}
          render={({ field: { onBlur, value } }) => (
            <Pressable
              onPress={() => {
                setIsEmploymentSheetVisible(true)
              }}
            >
              <Input
                isEditable={false}
                placeholder={i18n.t('employmentDetails.egSelfEmp')}
                onBlur={onBlur}
                iconLeft={<Icon type="WorkOutlined" color={colors.grays[80]} />}
                iconRight={
                  <Icon
                    type="ArrowDown"
                    color={colors.grays[80]}
                    className="mr-1 mt-1"
                  />
                }
                className="justify-center"
                style={{
                  color: colors.grays[100],
                  fontFamily: 'Graphie-Book',
                }}
                value={value}
                pointerEvents="none"
              />
            </Pressable>
          )}
          name="employmentType"
        />
        {errors?.employmentType?.message ? (
          <Stack className="mb-3">
            <B5 className="text-error-100">
              {errors?.employmentType?.message}
            </B5>
          </Stack>
        ) : null}

        {isEmploymentSheetVisible && (
          <BottomSheet
            visible={isEmploymentSheetVisible}
            onDismiss={closeBottomsheet}
            snapPoints={[400, 600]}
          >
            <>
              <XStack className="border-grays-30 items-center justify-between border-b px-3 py-2">
                <XStack className="items-center">
                  <Icon type="WorkOutlined" size={24} />
                  <SH1 className="ml-2">{i18n.t('employmentDetails.type')}</SH1>
                </XStack>
                <Pressable onPress={closeBottomsheet}>
                  <Icon size={24} type="Close" />
                </Pressable>
              </XStack>
              <Stack className="px-3">
                <Select onChange={selectEmploymentType}>
                  {criteriaData &&
                    criteriaData
                      ?.find(
                        (d: { criteriaName: string }) =>
                          d?.criteriaName === 'employmentType',
                      )
                      ?.fields?.map(({ key, displayValue }) => (
                        <Select.Option
                          key={key}
                          name={displayValue}
                          selectedValue={employmentType}
                          optionContainerStyle="mt-3 w-full justify-center rounded-lg py-3"
                        >
                          <SH1>{displayValue}</SH1>
                        </Select.Option>
                      ))}
                </Select>
              </Stack>
            </>
          </BottomSheet>
        )}
      </Stack>

      <Stack className="mt-3">
        <B2 className="text-grays-80 mb-1">
          {i18n.t('webFlow.employmentDetails.annual')}
          <B1 className="text-error-100 pl-1">*</B1>
        </B2>

        <Controller
          control={control}
          render={({ field: { onChange, onBlur } }) => (
            <Input
              maxLength={8}
              placeholder="Eg: ₹14,475"
              onBlur={onBlur}
              onChangeText={(value) => {
                onChange(value)
                setAnnualIncome(value)
              }}
              keyboardType="number-pad"
              value={annualIncome}
              type={`${errors?.income?.message ? 'error' : 'primary'}`}
            />
          )}
          name="income"
        />
        <FormValidationErrorMessage
          error={errors?.income?.message}
          key={errors?.income?.message}
        />
      </Stack>

      <Stack className="mt-3">
        <B2 className="text-grays-80 mb-1">
          {i18n.t('webFlow.basicDetails.dob')}
          <B1 className="text-error-100 ml-1">*</B1>
        </B2>
        <Controller
          control={control}
          name="dob"
          render={({ field: { value, onChange } }) => (
            <>
              <DatePicker
                selected={value}
                onChange={(date) => onChange(date)}
                customInput={<DateField />}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                maxDate={subYears(new Date(), 15)}
              />
            </>
          )}
        />
      </Stack>
    </Stack>
  )
}

export { FormFields }
